import React, { useState } from "react";
import { FaChevronDown, FaChevronUp, FaChevronRight, FaTimes } from "react-icons/fa";

const ProductsSidebar = ({ 
  onClose, 
  filters, 
  onFilter, 
  onReset,
  subcategories,
  onSubcategorySelect,
  selectedSubcategory 
}) => {
  // State for toggling sections
  const [isBrandOpen, setBrandOpen] = useState(true);
  const [isPriceOpen, setPriceOpen] = useState(true);
  const [isRatingOpen, setRatingOpen] = useState(true);
  const [isSubcategoryOpen, setSubcategoryOpen] = useState(true);
  
  // State for tracking which subcategories are expanded
  const [openSubcategories, setOpenSubcategories] = useState({});
  
  // State for selected filters
  const [selected, setSelected] = useState({
    brands: [],
    price: null,
    rating: null,
  });

  // Toggle subcategory expansion
  const toggleSubcategory = (subcatId) => {
    setOpenSubcategories(prev => ({
      ...prev,
      [subcatId]: !prev[subcatId]
    }));
  };

  // Apply filters
  const applyFilters = () => {
    onFilter(selected);
    if (onClose) onClose();
  };

  // Reset filters
  const resetFilters = () => {
    setSelected({ brands: [], price: null, rating: null });
    onReset();
    if (onClose) onClose();
  };

  // Render subcategories recursively
  const renderSubcategories = (subcategories, level = 0) => {
    return subcategories.map((subcat) => (
      <div key={subcat.id} className={`ml-${level * 2}`}>
        <div className="flex items-center py-1">
          {subcat.subcategories?.length > 0 ? (
            <button 
              onClick={() => toggleSubcategory(subcat.id)}
              className="mr-1 text-gray-500 hover:text-gray-700"
              aria-label={`Toggle ${subcat.name}`}
            >
              {openSubcategories[subcat.id] ? 
                <FaChevronDown size={12} /> : 
                <FaChevronRight size={12} />}
            </button>
          ) : (
            <span className="w-4 mr-1"></span> // Spacer for alignment
          )}
          <label className="flex items-center flex-grow cursor-pointer hover:bg-gray-50 px-2 py-1 rounded">
            <input
              type="radio"
              name="subcategory"
              checked={selectedSubcategory === subcat.id}
              onChange={() => onSubcategorySelect(subcat.id)}
              className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">{subcat.name}</span>
          </label>
        </div>
        
        {subcat.subcategories?.length > 0 && openSubcategories[subcat.id] && (
          <div className="ml-4 border-l border-gray-200 pl-2">
            {renderSubcategories(subcat.subcategories, level + 1)}
          </div>
        )}
      </div>
    ));
  };

  return (
    <div className="bg-white rounded-lg shadow-md relative p-4 h-full overflow-y-auto">
      {/* Close button for mobile */}
      <button
        className="md:hidden absolute top-2 right-2 text-gray-500 hover:text-gray-700"
        onClick={onClose}
        aria-label="Close sidebar"
      >
        <FaTimes />
      </button>

      {/* Subcategories Section */}
      <div className="mb-6">
        <div
          className="flex justify-between items-center cursor-pointer py-2"
          onClick={() => setSubcategoryOpen(!isSubcategoryOpen)}
        >
          <h3 className="font-semibold text-gray-900">Subcategories</h3>
          {isSubcategoryOpen ? <FaChevronUp /> : <FaChevronDown />}
        </div>
        
        {isSubcategoryOpen && (
          <div className="mt-2 space-y-1">
            {renderSubcategories(subcategories)}
          </div>
        )}
      </div>

      {/* Brands Section */}
      <div className="mb-6">
        <div
          className="flex justify-between items-center cursor-pointer py-2"
          onClick={() => setBrandOpen(!isBrandOpen)}
        >
          <h3 className="font-semibold text-gray-900">Brands</h3>
          {isBrandOpen ? <FaChevronUp /> : <FaChevronDown />}
        </div>
        
        {isBrandOpen && (
          <div className="ml-4 mt-2 space-y-2">
            {filters.brands?.map((brand) => (
              <label key={brand} className="flex items-center">
                <input
                  type="checkbox"
                  checked={selected.brands.includes(brand)}
                  onChange={() =>
                    setSelected(prev => ({
                      ...prev,
                      brands: prev.brands.includes(brand)
                        ? prev.brands.filter(b => b !== brand)
                        : [...prev.brands, brand],
                    }))
                  }
                  className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">{brand}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Price Section */}
      <div className="mb-6">
        <div
          className="flex justify-between items-center cursor-pointer py-2"
          onClick={() => setPriceOpen(!isPriceOpen)}
        >
          <h3 className="font-semibold text-gray-900">Price</h3>
          {isPriceOpen ? <FaChevronUp /> : <FaChevronDown />}
        </div>
        
        {isPriceOpen && (
          <div className="ml-4 mt-2 space-y-2">
            {filters.priceRanges?.map((range, idx) => (
              <label key={idx} className="flex items-center">
                <input
                  type="radio"
                  name="price"
                  checked={selected.price?.label === range.label}
                  onChange={() => setSelected(prev => ({ ...prev, price: range }))}
                  className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">{range.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Ratings Section */}
      <div className="mb-6">
        <div
          className="flex justify-between items-center cursor-pointer py-2"
          onClick={() => setRatingOpen(!isRatingOpen)}
        >
          <h3 className="font-semibold text-gray-900">Ratings</h3>
          {isRatingOpen ? <FaChevronUp /> : <FaChevronDown />}
        </div>
        
        {isRatingOpen && (
          <div className="ml-4 mt-2 space-y-2">
            {filters.ratings?.map((rate) => (
              <label key={rate} className="flex items-center">
                <input
                  type="radio"
                  name="rating"
                  checked={selected.rating === rate}
                  onChange={() => setSelected(prev => ({ ...prev, rating: rate }))}
                  className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">{rate}★ & above</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="sticky bottom-0 bg-white pt-4 pb-2">
        <button
          onClick={applyFilters}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md transition-colors duration-200"
        >
          Apply Filters
        </button>
        <button
          onClick={resetFilters}
          className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-4 rounded-md mt-2 transition-colors duration-200"
        >
          Reset Filters
        </button>
      </div>
    </div>
  );
};

export default ProductsSidebar;